import { useEffect } from "react";
import { useRouter } from "next/router";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { Layout } from "@components/Layout/index";
import {
  getAllDocumentsByType,
  getCollection,
  getModalSettings,
  getGlobalConfig,
  getPopupMailers,
} from "@lib/api/calls";
import { collectionBySlug } from "@lib/api/queries";
import { usePreviewSubscription } from "@lib/sanity";
import { getCollectionFromRedis } from "@lib/redis";
import { trackEvent } from "@lib/track-event";
import { NavigationGroup } from "@components/sections";
import { Redirect } from "@components/Redirect";
import { Header } from "@components/Collection/Header";
import { Products } from "@components/Collection/Products";
import { usePopulateFilters } from "@components/Filter/hooks/usePopulateFilters";
import { useFilterSort } from "@components/Filter/hooks/useFilterSort";
import { TYPE, SIZE, COLOUR_TAGS } from "@components/Filter/constants";
import { useProductsFromCollection } from "@components/Collection/hooks/useProductsFromCollection";

export default function Collection({
  preview,
  title,
  seo,
  data = {},
  initialProducts,
  config,
  subscribed,
  modal,
  popupMailers,
}) {
  const router = useRouter();
  const { data: collection } = usePreviewSubscription(collectionBySlug, {
    params: { handle: data?.collection?.slug },
    initialData: data && data.collection,
    enabled: preview || router.query.preview !== null,
  });

  const { productsFromCollection, state, dispatch } = useProductsFromCollection(
    collection,
    initialProducts
  );

  const { filteredValues, filterLabels, sortBy, setSortBy, onChange, clearFilters } =
    usePopulateFilters(productsFromCollection);

  const { productList } = useFilterSort({
    products: productsFromCollection,
    filteredValues,
    sortBy,
  });

  useEffect(() => {
    trackEvent("view_page", {
      pageTitle: title,
      pageCategory: "Collection Page",
      pageLocation: window.location.href,
      pagePath: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    if (collection) {
      trackEvent("view_collection", collection);
    }
  }, [collection]);

  if (collection && collection.explicitRedirect) {
    return (
      <Layout
        config={config}
        title={title}
        seo={seo}
        subscribed={subscribed}
        pageFound={collection !== null}
        modal={modal}
        isPreview={preview}
        popupMailers={popupMailers}
      >
        <Redirect
          type="collection"
          to={collection.explicitRedirect.to}
          label={collection.explicitRedirect.toLabel}
        />
      </Layout>
    );
  }

  return (
    <Layout
      config={config}
      title={title}
      seo={seo}
      subscribed={subscribed}
      pageFound={collection !== null}
      modal={modal}
      isPreview={preview}
      popupMailers={popupMailers}
    >
      {collection && <Header title={title} description={collection.description} />}
      <Products
        collection={collection}
        productList={productList.length > 0 ? productList : initialProducts}
        state={state}
        dispatch={dispatch}
        filteredValues={filteredValues}
        clearFilters={clearFilters}
        activeFilters={[TYPE, SIZE, COLOUR_TAGS]}
        filterLabels={filterLabels}
        onChange={onChange}
        sortBy={sortBy}
        setSortBy={setSortBy}
      />

      {collection && collection.navigation && collection.navigation.linkgroup && (
        <NavigationGroup
          {...collection.navigation.linkgroup[0]}
          collectionTitle={title}
          description={collection.navigation_description}
        />
      )}
    </Layout>
  );
}
export async function getStaticPaths({ locales }) {
  const allCollections = await getAllDocumentsByType("collectionPage");
  const paths = [];

  allCollections.forEach((collection) => {
    // Add default locale path (en)
    paths.push({
      params: {
        handle: collection.slug,
      },
    });

    // Add paths for other locales
    locales.forEach((locale) => {
      if (locale !== "en") {
        paths.push({
          params: {
            handle: collection.slug,
          },
          locale,
        });
      }
    });
  });

  return {
    paths,
    fallback: true,
  };
}

function urlToSafeString(url) {
  let converted = "";
  if (url) {
    converted = url.split("?")[0];
  }

  return converted;
}

export async function getStaticProps({ params, preview = false, locale }) {
  const collectionPage = await getCollection(urlToSafeString(params.handle), locale, preview);
  const initialProducts = await getCollectionFromRedis(params.handle, 1); 
  const modal = await getModalSettings(locale, preview);
  const config = await getGlobalConfig(locale, preview);
  
  // Get the base URL for the current locale
  const baseUrl = locale === 'gb' ? '/gb' : '';
  const popupMailers = await getPopupMailers(preview, baseUrl);

  const seo = {
    seoTitle: collectionPage?.seoTitle || `${collectionPage?.title} by Kotn`,
    seoDescription: collectionPage?.seoDescription || collectionPage?.collectionDescription || null,
    openGraphImage: collectionPage?.openGraphImage || null,
    twitterCard: collectionPage?.seoTwitterCardStyle || "summary_large_image",
  };

  return {
    props: {
      preview,
      title: collectionPage?.title || "",
      seo,
      data: { collection: collectionPage } || null,
      initialProducts,
      modal,
      config,
      popupMailers,
      ...(await serverSideTranslations(locale, [
        "common",
        "navbar",
        "footer",
        "productcard",
        "cart",
        "filter",
        "collectionpage",
        "recommendations",
      ])),
    },
    revalidate: 60,
  };
}